import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, Clock, Edit2, Trash2 } from 'lucide-react';

interface EndNodeData {
  label: string;
  status: 'success' | 'error' | 'pending' | 'approval';
  description?: string;
  onEdit?: () => void;
  onDelete?: () => void;
}

function EndNode({ data, selected }: NodeProps<EndNodeData>) {
  const getStatusConfig = () => {
    switch (data.status) {
      case 'success':
        return {
          icon: CheckCircle2,
          color: 'green',
          bgGradient: 'from-green-500 to-green-600',
          borderColor: selected ? 'border-green-500 shadow-green-200' : 'border-green-300',
        };
      case 'error':
        return {
          icon: XCircle,
          color: 'red',
          bgGradient: 'from-red-500 to-red-600',
          borderColor: selected ? 'border-red-500 shadow-red-200' : 'border-red-300',
        };
      case 'pending':
        return {
          icon: Clock,
          color: 'yellow',
          bgGradient: 'from-yellow-500 to-yellow-600',
          borderColor: selected ? 'border-yellow-500 shadow-yellow-200' : 'border-yellow-300',
        };
      case 'approval':
        return {
          icon: Clock,
          color: 'orange',
          bgGradient: 'from-orange-500 to-orange-600',
          borderColor: selected ? 'border-orange-500 shadow-orange-200' : 'border-orange-300',
        };
      default:
        return {
          icon: CheckCircle2,
          color: 'green',
          bgGradient: 'from-green-500 to-green-600',
          borderColor: selected ? 'border-green-500 shadow-green-200' : 'border-green-300',
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <div
      className={`bg-white rounded-lg border-2 shadow-lg min-w-[200px] max-w-[280px] ${config.borderColor}`}
    >
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        className={`w-3 h-3 !bg-${config.color}-500`}
      />

      {/* Header */}
      <div className={`bg-gradient-to-r ${config.bgGradient} text-white px-3 py-2 rounded-t-md flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" />
          <span className="font-semibold text-sm">END</span>
        </div>
        <div className="flex gap-1 nodrag nopan" style={{ pointerEvents: 'auto' }}>
          {data.onEdit && (
            <button
              type="button"
              className={`h-6 w-6 p-1 flex items-center justify-center hover:bg-${config.color}-600/20 rounded transition-colors cursor-pointer nodrag`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                console.log('✏️ Edit clicked!', data.label);
                if (data.onEdit) {
                  data.onEdit();
                }
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              title="Edit node"
            >
              <Edit2 className="w-3 h-3 text-white" style={{ pointerEvents: 'none' }} />
            </button>
          )}
          {data.onDelete && (
            <button
              type="button"
              className="h-6 w-6 p-1 flex items-center justify-center hover:bg-red-600/20 rounded transition-colors cursor-pointer nodrag"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                console.log('🗑️ Delete clicked!', data.label);
                if (data.onDelete) {
                  data.onDelete();
                }
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              title="Delete node"
            >
              <Trash2 className="w-3 h-3 text-white" style={{ pointerEvents: 'none' }} />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-3 space-y-2">
        <div className="font-semibold text-sm text-gray-900">{data.label}</div>
        
        {data.description && (
          <div className="text-xs text-gray-600">{data.description}</div>
        )}

        <div className="pt-2 border-t">
          <Badge 
            variant="secondary" 
            className={`text-xs bg-${config.color}-50 text-${config.color}-700 border-${config.color}-200`}
          >
            {data.status.charAt(0).toUpperCase() + data.status.slice(1)}
          </Badge>
        </div>
      </div>
    </div>
  );
}

export default memo(EndNode);

